import { useFlags } from '@atlaskit/flag';
import { RunLocationsApiService, RunsApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useDocument } from 'core/hooks/useDocument';
import { deleteItem } from 'core/utilities/api-wrapper';
import { IRunDao, IRunLocationDao } from 'core/api/types/runs.interface';
import AddEditRunsDialog from './add-edit-runs-dialog';
import AddEditRunLocationsDialog from './add-edit-run-locations-dialog';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import DragHandleVerticalIcon from '@atlaskit/icon/core/drag-handle-vertical';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useAuthState } from 'core/providers/AuthProvider';
import { DaysOfWeekOptions } from 'core/constants/days-of-week';
import { where } from 'firebase/firestore';
import { useCollection } from 'core/hooks/useCollection';
import { useEffect, useMemo, useState } from 'react';
import { ILocationDao } from 'core/api/types';
import { ISubLocationDao } from 'core/api/types/sub-locations.interface';
import { useSharedData } from 'core/providers/SharedDataProvider';

const RunsSettingItem = () => {
  const { uid } = useParams();
  const flags = useFlags();
  const dialog = useDialog();
  const { userData } = useAuthState();
  const { locations, subLocations } = useSharedData();

  const [locationItems, setLocationItems] = useState<ILocationDao[]>([]);
  const [subLocationItems, setSubLocationItems] = useState<ISubLocationDao[]>([]);

  const runLocationConstraints = useMemo(() => [where('runId', '==', uid)], [uid]);

  const { tableRows: runLocations, loading: runLocationsLoading } = useCollection<IRunLocationDao>({
    apiService: RunLocationsApiService,
    constraints: runLocationConstraints,
    initialLimit: 1000,
  });

  const { data: run, loading: runLoading } = useDocument<IRunDao>({
    apiService: RunsApiService,
    uid,
    navigateTo: '/settings/runs',
  });

  useEffect(() => {
    if (locations) {
      setLocationItems(locations);
    }
  }, [locations]);

  useEffect(() => {
    if (subLocations) {
      setSubLocationItems(subLocations);
    }
  }, [subLocations]);

  const RunsItemDetails = ({ run }: { run: IRunDao }) => {
    const RunDetails = [
      {
        label: 'Name',
        value: run.name,
        key: 'name',
      },
      {
        label: 'Notes',
        value: run.notes,
        key: 'notes',
      },
      {
        label: 'Created at',
        value: run.created.at ? dayjs(run.created.at.toDate()).format('HH:mm, DD/MM/YYYY') : 'Unknown',
        key: 'createdAt',
      },
      {
        label: 'Created by',
        value: run.created.by.fullName ?? 'Unknown',
        key: 'createdBy',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[300px] bg-white shadow-md rounded-md h-fit'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
          <div className='flex space-x-2'>
            <SharedButton
              onClick={() => dialog?.openDialog(<AddEditRunsDialog run={run} />)}
              type='button'
              appearance='primary'
              label='Update'
            />
            <SharedButton
              onClick={() =>
                dialog?.openDialog(
                  <ConfirmationDialog
                    title='Delete run'
                    textContent='Are you sure you want to delete this run? This action cannot be undone.'
                    action={() => deleteItem(RunsApiService, run, flags, dialog)}
                    buttonLabel='Delete Run'
                  />
                )
              }
              type='button'
              appearance='danger'
              label='Delete'
            />
          </div>
        </div>
        <div className='p-4'>
          {RunDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              <p className='body-01 break-words'>{section.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const RunLocationsTableColumns: ISharedTableColumn[] = [
    { label: '', key: 'drag', templateId: 'drag', width: 1, isCompact: true },
    { label: 'Location', key: 'location', templateId: 'location', width: 10 },
    { label: 'Sub Location', key: 'subLocation', templateId: 'subLocation', width: 10 },
    { label: 'Notes', key: 'notes', templateId: 'text', width: 10 },
    { label: 'Days of Week', key: 'daysOfWeek', templateId: 'daysOfWeek', width: 10 },
    { label: '', key: 'actions', templateId: 'actions', width: 1, isCompact: true },
  ];

  const locationCellTemplate = (runLocation: IRunLocationDao) => {
    return <p>{locationItems.find((location) => location.uid === runLocation.locationId)?.name || '-'}</p>;
  };

  const subLocationCellTemplate = (runLocation: IRunLocationDao) => {
    return <p>{subLocationItems.find((subLocation) => subLocation.uid === runLocation.subLocationId)?.name || '-'}</p>;
  };

  const dragCellTemplate = (runLocation: IRunLocationDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <DragHandleVerticalIcon label='Drag' />
      </div>
    );
  };

  const daysOfWeekCellTemplate = (runLocation: IRunLocationDao) => {
    return (
      <p>
        {runLocation.daysOfWeek
          .map((day) => DaysOfWeekOptions.find((option) => option.value === day)?.label.slice(0, 2) || day)
          .join(', ')}
      </p>
    );
  };

  const actionsCellTemplate = (runLocation: IRunLocationDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() => dialog?.openDialog(<AddEditRunLocationsDialog run={run!} runLocation={runLocation} />)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Update'
        />
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ConfirmationDialog
                title='Delete run location'
                textContent='Are you sure you want to delete this run location? This action cannot be undone.'
                action={() => deleteItem(RunLocationsApiService, runLocation, flags, dialog)}
                buttonLabel='Delete Run Location'
              />
            )
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Delete'
        />
      </div>
    );
  };
  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: locationCellTemplate, id: 'location' },
    { template: subLocationCellTemplate, id: 'subLocation' },
    { template: dragCellTemplate, id: 'drag' },
    { template: daysOfWeekCellTemplate, id: 'daysOfWeek' },
    { template: actionsCellTemplate, id: 'actions' },
  ];

  const handleReorder = async (sourceIndex: number, destinationIndex: number) => {
    if (!run) return;

    try {
      const reorderedLocations = [...run.locations];

      const [movedItem] = reorderedLocations.splice(sourceIndex, 1);

      reorderedLocations.splice(destinationIndex, 0, movedItem);

      const updatedLocations = reorderedLocations;

      await RunsApiService.update(
        run.uid,
        {
          locations: updatedLocations,
        },
        {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        }
      );

      showSuccessFlag('Order updated', 'The run locations have been reordered successfully', flags);
    } catch (error) {
      showErrorFlag('Update failed', 'Unable to reorder the run locations, please try again.', flags);
    }
  };

  const sortedTableRows = useMemo(() => {
    if (!run || !runLocations) return runLocations;

    // Create a map of location IDs to their index in run.locations
    const locationOrderMap = new Map<string, number>();
    run.locations.forEach((locationId, index) => {
      locationOrderMap.set(locationId, index);
    });

    // Sort runLocations based on the order in run.locations
    return [...runLocations].sort((a, b) => {
      const indexA = locationOrderMap.get(a.data.uid) ?? Infinity;
      const indexB = locationOrderMap.get(b.data.uid) ?? Infinity;
      return indexA - indexB;
    });
  }, [runLocations, run]);

  return (
    <>
      <>
        {!run || runLoading ? (
          <PageHeaderSkeleton actions={[]} subtitle={true} />
        ) : (
          <>
            <PageHeader title={`${run.name}`} showBack={true} />
            <div className='flex flex-row py-6 space-x-6'>
              <RunsItemDetails run={run} />
              <div className='flex-grow min-w-0'>
                <div className='relative'>
                  <div className='flex relative z-10 flex items-center justify-between pb-3'>
                    <div>Locations</div>
                    <SharedButton
                      onClick={() => dialog?.openDialog(<AddEditRunLocationsDialog run={run} />)}
                      type='button'
                      appearance='primary'
                      label='Add Location'
                    />
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                </div>
                <div className='mt-6'>
                  <SharedTable
                    columns={RunLocationsTableColumns}
                    rows={sortedTableRows}
                    isLoading={runLocationsLoading}
                    customTemplates={customTemplates}
                    draggable={true}
                    onReorder={handleReorder}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default RunsSettingItem;
